import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Rnd } from 'react-rnd';
import {
  Card,
  CardContent,
  Typography,
  CardActions,
  IconButton,
  Menu,
  MenuItem,
  CardMedia,
  Link,
  Tooltip,
  Snackbar,
  Button,
  Box,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { makeStyles } from 'tss-react/mui';

import CloseIcon from '@mui/icons-material/Close';
import RouteIcon from '@mui/icons-material/Route';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ErrorIcon from '@mui/icons-material/Error';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import Battery60Icon from '@mui/icons-material/Battery60';
import BatteryCharging60Icon from '@mui/icons-material/BatteryCharging60';
import Battery20Icon from '@mui/icons-material/Battery20';
import BatteryCharging20Icon from '@mui/icons-material/BatteryCharging20';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExploreIcon from '@mui/icons-material/Explore';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MapIcon from '@mui/icons-material/Map';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { useTranslation } from './LocalizationProvider';
import RemoveDialog from './RemoveDialog';
import PositionValue from './PositionValue';
import { useRestriction, useManager } from '../util/permissions';
import { devicesActions } from '../../store';
import { useCatch, useCatchCallback } from '../../reactHelper';
import { useAttributePreference } from '../util/preferences';
import fetchOrThrow from '../util/fetchOrThrow';
import { openUrl } from '../util/navigationFallback';
import { buildMapsLinks, reportMapOpenFailure } from '../util/mapLinks';
import EngineIcon from '../../resources/images/data/engine.svg?react';

const FLOATING_CARD_WIDTH = 440;
const FLOATING_CARD_MARGIN = 16;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getViewportSafePos = (inputPos, cardWidth = FLOATING_CARD_WIDTH) => {
  if (typeof window === 'undefined') return undefined;

  const viewportWidth = window.innerWidth || 1280;
  const viewportHeight = window.innerHeight || 800;

  const safeX = clamp(
    Number.isFinite(inputPos?.x) ? inputPos.x : viewportWidth - cardWidth - FLOATING_CARD_MARGIN,
    FLOATING_CARD_MARGIN,
    Math.max(FLOATING_CARD_MARGIN, viewportWidth - cardWidth - FLOATING_CARD_MARGIN),
  );

  const estimatedHeight = Math.min(680, Math.max(420, viewportHeight * 0.72));
  const safeY = clamp(
    Number.isFinite(inputPos?.y) ? inputPos.y : viewportHeight - estimatedHeight - FLOATING_CARD_MARGIN,
    FLOATING_CARD_MARGIN,
    Math.max(FLOATING_CARD_MARGIN, viewportHeight - estimatedHeight - FLOATING_CARD_MARGIN),
  );

  return { x: safeX, y: safeY };
};

const useStyles = makeStyles()(
  (
    theme,
    {
      desktopPadding,
      dense,
      dragging,
      docked,
      isMobile,
      isTablet,
      isDesktop,
    },
  ) => {
    const popupMaxWidth = Math.max(420, theme.dimensions?.popupMaxWidth || FLOATING_CARD_WIDTH);
    const popupImageHeight = theme.dimensions?.popupImageHeight || 180;

    return {
      root: docked
        ? {
            pointerEvents: 'auto',
            position: 'relative',
            display: 'block',
            padding: 0,
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            minHeight: 0,
            minWidth: 0,
          }
        : isMobile || isTablet
          ? {
              pointerEvents: 'auto',
              position: 'relative',
              display: 'block',
              width: '100%',
              maxWidth: '100%',
              height: '100%',
              minHeight: 0,
              minWidth: 0,
            }
          : {
              pointerEvents: 'none',
              position: 'fixed',
              zIndex: 5,
              left: isDesktop ? `calc(50% + ${desktopPadding} / 2)` : '50%',
              transform: 'translateX(-50%)',
              width: 'auto',
              maxWidth: '100vw',
              bottom: theme.spacing(3),
            },

      card: {
        pointerEvents: 'auto',
        width: docked || isMobile || isTablet ? '100%' : popupMaxWidth,
        minWidth: docked || isMobile || isTablet ? 0 : 420,
        maxWidth: docked || isMobile || isTablet ? '100%' : 460,
        overflow: 'hidden',
        borderRadius: docked ? 0 : isMobile ? 20 : 16,
        boxShadow: docked ? 'none' : (isMobile || isTablet ? '0 6px 22px rgba(0,0,0,0.12)' : '0 10px 28px rgba(0,0,0,0.10)'),
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
        minHeight: 0,
      },

      media: {
        height: docked ? (isMobile ? 96 : 120) : (isMobile ? 120 : popupImageHeight),
        display: 'block',
        flexShrink: 0,
      },

      header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(docked ? 1 : 1.25, 1.25, 0.5, docked ? 1.25 : 1.75),
        userSelect: 'none',
        minHeight: docked ? 52 : 58,
        gap: theme.spacing(1),
        flexShrink: 0,
        background: theme.palette.background.paper,
      },

      headerIdentity: {
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flex: 1,
      },

      headerTitleWrap: {
        minWidth: 0,
        flex: 1,
      },

      headerTitle: {
        minWidth: 0,
        fontWeight: 750,
        fontSize: isMobile ? '1.06rem' : (docked ? '1.12rem' : '1.2rem'),
        letterSpacing: 0.1,
        color: theme.palette.text.primary,
        lineHeight: 1.18,
      },

      headerSubtitle: {
        marginTop: theme.spacing(0.25),
        color: theme.palette.text.secondary,
        fontSize: '0.78rem',
        lineHeight: 1.25,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },

      statusDot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: '#ff8a00',
        boxShadow: `0 0 0 4px ${alpha('#ff8a00', 0.12)}`,
        flexShrink: 0,
      },

      headerButtons: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.25),
        color: theme.palette.text.secondary,
      },

      hero: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        padding: theme.spacing(1, docked ? 1.25 : 1.75, 1.1),
        background: theme.palette.background.paper,
        flexShrink: 0,
      },

      heroState: {
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.text.secondary,
      },

      heroStateIcon: {
        width: 30,
        height: 30,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: alpha('#ff8a00', 0.14),
        color: '#ff8a00',
        flexShrink: 0,
        '& svg': {
          fontSize: '1.15rem',
        },
      },

      heroStateLabel: {
        minWidth: 0,
        color: theme.palette.text.secondary,
        fontWeight: 600,
        lineHeight: 1.25,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },

      heroMetrics: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(0.8),
        flexShrink: 0,
        color: theme.palette.text.primary,
        whiteSpace: 'nowrap',
      },

      speedText: {
        fontWeight: 800,
        fontSize: docked || isMobile ? '1.15rem' : '1rem',
        whiteSpace: 'nowrap',
      },

      heroDivider: {
        height: 1,
        background: alpha(theme.palette.divider, 0.8),
        margin: theme.spacing(0, docked ? 1.25 : 1.75),
        flexShrink: 0,
      },

      decisionGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: theme.spacing(0.75),
        marginBottom: theme.spacing(1),
        [theme.breakpoints.down('sm')]: {
          gridTemplateColumns: '1fr',
        },
      },

      decisionChip: {
        minWidth: 0,
        border: `1px solid ${alpha(theme.palette.divider, 0.72)}`,
        borderRadius: 14,
        background: alpha(theme.palette.background.paper, 0.82),
        padding: theme.spacing(0.75, 0.9),
      },

      chipLabel: {
        color: theme.palette.text.secondary,
        fontSize: '0.72rem',
        lineHeight: 1.2,
      },

      chipValue: {
        marginTop: theme.spacing(0.25),
        fontWeight: 700,
        fontSize: '0.86rem',
        lineHeight: 1.2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },

      addressCard: {
        border: `1px solid ${alpha(theme.palette.divider, 0.72)}`,
        borderRadius: 16,
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1),
        background: alpha(theme.palette.background.paper, 0.88),
      },

      addressActions: {
        display: 'flex',
        gap: theme.spacing(0.75),
        flexWrap: 'wrap',
        marginTop: theme.spacing(0.75),
      },

      technicalToggle: {
        width: '100%',
        justifyContent: 'space-between',
        textTransform: 'none',
        color: theme.palette.text.primary,
        borderColor: alpha(theme.palette.divider, 0.72),
        borderRadius: 12,
      },

      topActionsBar: {
        display: 'grid',
        gridTemplateColumns: isMobile || isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
        gap: theme.spacing(isMobile ? 0.75 : 1),
        marginBottom: theme.spacing(isMobile ? 0.85 : 1.25),
        minWidth: 0,
        [theme.breakpoints.down('sm')]: {
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
      },

      actionTile: {
        minWidth: 0,
        width: '100%',
        height: isMobile ? 58 : 64,
        minHeight: isMobile ? 54 : 60,
        padding: theme.spacing(isMobile ? 0.45 : 0.65, 0.5),
        borderRadius: isMobile ? 12 : 14,
        borderColor: alpha(theme.palette.divider, 0.85),
        background: alpha(theme.palette.background.paper, 0.96),
        color: '#ff4b14',
        textTransform: 'none',
        fontWeight: 600,
        boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.05)}`,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.2),
        lineHeight: 1.1,
        fontSize: isMobile ? '0.72rem' : '0.75rem',
        whiteSpace: 'normal',
        textAlign: 'center',
        '& .MuiButton-startIcon': {
          margin: 0,
          '& svg': { fontSize: isMobile ? 20 : 22 },
        },
        '& .MuiButton-label, & .MuiButton-endIcon + span': {
          minWidth: 0,
        },
      },

      content: {
        paddingTop: theme.spacing(docked ? 1 : 1.25),
        paddingBottom: theme.spacing(1),
        paddingLeft: theme.spacing(docked ? 1.25 : 1.75),
        paddingRight: theme.spacing(docked ? 1.25 : 1.75),
        overflowY: 'auto',
        overflowX: 'hidden',
        minHeight: 0,
        flex: 1,
        WebkitOverflowScrolling: 'touch',
      },

      detailsGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(118px, 0.8fr) minmax(0, 1.2fr)',
        gap: theme.spacing(0.75, 1.25),
        padding: theme.spacing(1.25),
        marginBottom: theme.spacing(1.25),
        border: 0,
        borderRadius: 14,
        background: alpha(theme.palette.background.paper, 0.94),
        [theme.breakpoints.down('sm')]: {
          gridTemplateColumns: 'minmax(98px, 0.75fr) minmax(0, 1.25fr)',
          gap: theme.spacing(0.7, 1),
          padding: theme.spacing(1),
        },
      },

      detailLabel: {
        minWidth: 0,
        color: theme.palette.text.secondary,
        fontSize: '0.78rem',
        lineHeight: 1.35,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },

      detailValue: {
        minWidth: 0,
        color: theme.palette.text.primary,
        fontWeight: 600,
        lineHeight: 1.35,
        textAlign: 'right',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        '& a, & button': {
          maxWidth: '100%',
          color: 'inherit',
          font: 'inherit',
          fontWeight: 'inherit',
          textAlign: 'inherit',
        },
      },

      addressRow: {
        gridColumn: '1 / -1',
        minWidth: 0,
        margin: theme.spacing(0.25, 0, 0.5),
      },

      addressLabel: {
        color: theme.palette.text.secondary,
        fontSize: '0.78rem',
        lineHeight: 1.35,
        marginBottom: theme.spacing(0.35),
      },

      addressValue: {
        minWidth: 0,
        color: theme.palette.text.primary,
        fontWeight: 600,
        lineHeight: 1.35,
        textAlign: 'left',
        display: '-webkit-box',
        WebkitLineClamp: isMobile ? 4 : 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        '& a, & button': {
          color: 'inherit',
          font: 'inherit',
          fontWeight: 'inherit',
          textAlign: 'left',
          maxWidth: '100%',
        },
      },

      detailsLinkRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(0.25),
        marginBottom: theme.spacing(1.25),
      },

      statusBarWrap: {
        position: 'relative',
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        flexShrink: 0,
        background: theme.palette.background.paper,
      },

      statusBar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(isMobile ? 1 : 1.25),
        padding: theme.spacing(isMobile ? 0.85 : 0.7, 1.1, isMobile ? 0.95 : 0.8, 1.1),
        backgroundColor: alpha(theme.palette.background.paper, 0.96),
        color: theme.palette.text.primary,
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        overflowX: 'auto',
        overscrollBehaviorX: 'contain',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        cursor: isDesktop && dragging ? 'grabbing' : 'default',
        touchAction: 'pan-x',
      },

      statusIconGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.6),
        flexShrink: 0,
      },

      statusLabel: {
        fontSize: '0.75rem',
        textTransform: 'none',
        letterSpacing: 0,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
        display: dense ? 'none' : 'block',
      },

      statusIconLarge: {
        fontSize: isMobile ? '1.35rem' : '1.5rem',
        flexShrink: 0,
      },

      success: { color: '#28A745' },
      warning: { color: '#FFC107' },
      warningDark: { color: '#FD7E14' },
      error: { color: '#DC3545' },
      neutral: { color: theme.palette.text.secondary },
      info: { color: '#007BFF' },
      infoDark: { color: '#0B3D91' },
      purple: { color: '#6F42C1' },
      gray: { color: '#6C757D' },

      lockLocked: { color: theme.palette.error.main },
      lockUnlocked: { color: theme.palette.success.main },

      divider: {
        width: 1,
        height: 18,
        background: alpha(theme.palette.divider, 0.7),
        flexShrink: 0,
      },

      statusTailActions: {
        display: 'flex',
        gap: theme.spacing(0.2),
        flexShrink: 0,
      },

      scrollBtn: {
        position: 'sticky',
        bottom: 0,
        alignSelf: 'center',
        background: alpha(theme.palette.background.paper, 0.9),
        boxShadow: `0 0 0 1px ${alpha(theme.palette.divider, 0.6)}`,
        '&:hover': { background: alpha(theme.palette.background.paper, 1) },
        ...(isMobile && {
          display: 'none',
        }),
      },

      scrollBtnLeft: { left: 0, marginRight: theme.spacing(1) },
      scrollBtnRight: { right: 0, marginLeft: theme.spacing(1) },

      quickLinksWrap: {
        padding: theme.spacing(1, 0, 0, 0),
        marginTop: theme.spacing(0.25),
        background: 'transparent',
        minWidth: 0,
      },

      quickLinksTitle: {
        marginBottom: theme.spacing(0.75),
        fontWeight: 600,
      },

      quickLinksButtons: {
        display: 'flex',
        alignItems: 'stretch',
        gap: theme.spacing(0.75),
        flexWrap: 'wrap',
        minWidth: 0,
        '& > *': {
          flex: '1 1 140px',
          minWidth: 0,
        },
        ...(isMobile && {
          '& > *': {
            flex: '1 1 calc(50% - 6px)',
            minWidth: 0,
          },
        }),
      },

      quickBtn: {
        borderRadius: 10,
        textTransform: 'none',
        ...(isMobile && {
          width: '100%',
        }),
      },

      actions: {
        minHeight: isMobile ? 56 : 54,
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
        gap: theme.spacing(0.75),
        rowGap: 0,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        paddingBottom: theme.spacing(0.75),
        paddingTop: theme.spacing(0.5),
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.45)}`,
        background: theme.palette.background.paper,
        flexShrink: 0,
        '& .MuiIconButton-root': {
          minWidth: isMobile ? 42 : undefined,
          minHeight: isMobile ? 42 : undefined,
        },
        '& .MuiIconButton-root:focus-visible': {
          outline: `2px solid ${alpha(theme.palette.primary.main, 0.7)}`,
          outlineOffset: 2,
        },
      },

      footerPrimaryActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        minWidth: 0,
        flex: (isMobile || isTablet) ? 1 : '0 0 auto',
        '& > *': {
          flex: (isMobile || isTablet) ? '1 1 0' : '0 0 auto',
          minWidth: (isMobile || isTablet) ? 0 : undefined,
        },
      },

      footerSecondaryActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        marginLeft: (isMobile || isTablet) ? 0 : 'auto',
        flexShrink: 0,
      },

      footerButton: {
        borderRadius: 999,
        textTransform: 'none',
        minHeight: 40,
        whiteSpace: 'nowrap',
        fontWeight: 700,
        ...(isMobile && {
          paddingLeft: theme.spacing(1.15),
          paddingRight: theme.spacing(1.15),
        }),
      },

      commandButton: {
        borderRadius: 999,
        textTransform: 'none',
        minHeight: 40,
        whiteSpace: 'nowrap',
        fontWeight: 800,
        boxShadow: `0 6px 14px ${alpha(theme.palette.warning.main, 0.22)}`,
        backgroundColor: '#ff4b14',
        color: theme.palette.common.white,
        '&:hover': {
          backgroundColor: '#e63f0e',
          boxShadow: `0 8px 18px ${alpha(theme.palette.warning.main, 0.28)}`,
        },
        '&.Mui-disabled': {
          boxShadow: 'none',
        },
      },

      mobileGrip: {
        display: docked && isMobile ? 'flex' : 'none',
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: theme.spacing(0.75),
        paddingBottom: theme.spacing(0.25),
        background: theme.palette.background.paper,
        flexShrink: 0,
      },

      mobileGripBar: {
        width: 42,
        height: 5,
        borderRadius: 999,
        background: alpha(theme.palette.text.primary, 0.18),
      },
    };
  },
);

const safeClipboardWrite = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const detectLocked = (device, position) => {
  const devAttrs = device?.attributes || {};
  const posAttrs = position?.attributes || {};

  let raw = devAttrs.lock
    ?? devAttrs.blocked
    ?? devAttrs.bloqueado
    ?? devAttrs.engineBlocked
    ?? posAttrs.lock
    ?? posAttrs.blocked
    ?? posAttrs.bloqueado
    ?? posAttrs.engineBlocked
    ?? device?.locked
    ?? device?.blocked
    ?? position?.blocked;

  if (raw === undefined && posAttrs.output !== undefined) {
    raw = posAttrs.output;
  }

  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw === 1;
  if (typeof raw === 'string') {
    const v = raw.toLowerCase();
    return v === 'true'
      || v === '1'
      || v === 'locked'
      || v === 'on'
      || v === 'bloqueado';
  }
  return false;
};

const toKph = (knots) => {
  if (knots == null) return null;
  const n = Number(knots);
  if (Number.isNaN(n)) return null;
  return n * 1.852;
};

const hoursSince = (dateLike) => {
  if (!dateLike) return null;
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60));
};

const minutesSince = (dateLike) => {
  if (!dateLike) return null;
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60));
};

const formatRelativeTime = (dateLike) => {
  if (!dateLike) return 'sem comunicação';
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return 'sem comunicação';
  const diffMinutes = Math.max(0, Math.floor((Date.now() - d.getTime()) / (1000 * 60)));
  if (diffMinutes < 1) return 'atualizado agora';
  if (diffMinutes < 60) return `atualizado há ${diffMinutes} min`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `sem sinal há ${diffHours} h`;
  const diffDays = Math.floor(diffHours / 24);
  return `sem sinal há ${diffDays} d`;
};

const formatCardDate = (dateLike) => {
  if (!dateLike) return '—';
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const getVehicleState = (position, device, classes) => {
  const attrs = position?.attributes || {};
  const ignition = attrs.ignition;

  const lastUpdate = position?.fixTime
    || position?.deviceTime
    || position?.serverTime
    || position?.time
    || position?.timestamp
    || device?.lastUpdate
    || device?.lastUpdateTime
    || null;

  const h = hoursSince(lastUpdate);
  const noData = !lastUpdate;
  const stale72 = h != null && h >= 72;
  const stale24 = h != null && h >= 24 && h < 72;
  const speedKph = toKph(position?.speed);
  const moving = speedKph != null && speedKph > 2;
  const idle = ignition === true && !moving;

  const idleSecondsRaw = attrs.idleTime
    ?? attrs.idleSeconds
    ?? attrs.idleDuration
    ?? attrs.idleDurationSeconds
    ?? null;

  const idleMinutesFromAttr = idleSecondsRaw != null && !Number.isNaN(Number(idleSecondsRaw))
    ? Math.floor(Number(idleSecondsRaw) / 60)
    : null;
  const idleMinutesFallback = idle ? minutesSince(position?.fixTime || device?.lastUpdate) : null;
  const idleMinutes = idleMinutesFromAttr ?? idleMinutesFallback ?? null;
  const idleOver10 = idle && idleMinutes != null && idleMinutes >= 10;

  if (noData) {
    return {
      label: 'Sem dados',
      icon: <ErrorIcon className={`${classes.gray} ${classes.statusIconLarge}`} />,
      tone: 'neutral',
    };
  }
  if (stale72) {
    return {
      label: 'Desconectado > 72h',
      icon: <WifiOffIcon className={`${classes.purple} ${classes.statusIconLarge}`} />,
      tone: 'offline',
    };
  }
  if (stale24) {
    return {
      label: 'Desconectado > 24h',
      icon: <WifiOffIcon className={`${classes.infoDark} ${classes.statusIconLarge}`} />,
      tone: 'offline',
    };
  }
  if (ignition === false) {
    return {
      label: 'Ignição desligada',
      icon: <EngineIcon width={24} height={24} className={classes.error} />,
      tone: 'stopped',
    };
  }
  if (moving) {
    return {
      label: 'Em movimento',
      icon: <EngineIcon width={24} height={24} className={classes.success} />,
      tone: 'moving',
    };
  }
  if (idleOver10) {
    return {
      label: 'Ocioso > 10 min',
      icon: <PauseCircleFilledIcon className={`${classes.warningDark} ${classes.statusIconLarge}`} />,
      tone: 'idle',
    };
  }
  if (idle) {
    return {
      label: 'Parado com motor ligado',
      icon: <PauseCircleFilledIcon className={`${classes.warning} ${classes.statusIconLarge}`} />,
      tone: 'idle',
    };
  }
  if (ignition === true) {
    return {
      label: 'Ignição ligada',
      icon: <EngineIcon width={24} height={24} className={classes.success} />,
      tone: 'moving',
    };
  }
  return {
    label: 'Status desconhecido',
    icon: <EngineIcon width={24} height={24} className={classes.neutral} />,
    tone: 'neutral',
  };
};

const getBatteryIcon = (battery, charging, classes) => {
  if (battery === undefined) return null;
  if (battery > 70) {
    return charging
      ? <BatteryChargingFullIcon className={`${classes.success} ${classes.statusIconLarge}`} />
      : <BatteryFullIcon className={`${classes.success} ${classes.statusIconLarge}`} />;
  }
  if (battery > 30) {
    return charging
      ? <BatteryCharging60Icon className={`${classes.warning} ${classes.statusIconLarge}`} />
      : <Battery60Icon className={`${classes.warning} ${classes.statusIconLarge}`} />;
  }
  return charging
    ? <BatteryCharging20Icon className={`${classes.error} ${classes.statusIconLarge}`} />
    : <Battery20Icon className={`${classes.error} ${classes.statusIconLarge}`} />;
};

const DetailRow = React.memo(({
  label,
  children,
  address = false,
  isMobile = false,
}) => {
  const { classes } = useStyles({
    desktopPadding: 0,
    dense: false,
    dragging: false,
    docked: false,
    isMobile,
    isTablet: false,
    isDesktop: true,
  });

  if (address) {
    return (
      <Box className={classes.addressRow}>
        <Typography className={classes.addressLabel} title={label}>{label}</Typography>
        <Typography component="div" variant="body2" className={classes.addressValue}>
          {children}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Typography className={classes.detailLabel} title={label}>{label}</Typography>
      <Typography component="div" variant="body2" className={classes.detailValue}>
        {children}
      </Typography>
    </>
  );
});

const StatusCard = ({
  deviceId,
  position,
  onClose,
  onCollapse,
  disableActions = false,
  desktopPadding = 0,
  docked = false,
  mobileExpanded = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const shouldUseRnd = isDesktop && !docked;

  const [pinned, setPinned] = useState(() => localStorage.getItem('statusPinned') === '1');
  const [mapsAnchorEl, setMapsAnchorEl] = useState(null);
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [toast, setToast] = useState('');
  const [failedMapUrl, setFailedMapUrl] = useState('');
  const [technicalOpen, setTechnicalOpen] = useState(true);
  const [pos, setPos] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('statusCardPos') || '{}') || {};
      return getViewportSafePos(raw);
    } catch {
      return getViewportSafePos(undefined);
    }
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useTranslation();

  const { classes } = useStyles({
    desktopPadding,
    dense: false,
    dragging: false,
    docked,
    isMobile,
    isTablet,
    isDesktop,
  });

  const readonly = useRestriction('readonly');
  const manager = useManager();

  const shareDisabled = useSelector((state) => state.session.server.attributes.disableShare);
  const user = useSelector((state) => state.session.user);
  const device = useSelector((state) => state.devices.items[deviceId]);

  const canEditDevice = !disableActions && !readonly;
  const canRemoveDevice = !disableActions && manager;

  const deviceImage = device?.attributes?.deviceImage;

  const navigationAppLink = useAttributePreference('navigationAppLink');
  const navigationAppTitle = useAttributePreference('navigationAppTitle');

  useEffect(() => {
    if (!isDesktop) {
      setPos(undefined);
      return;
    }

    if (!docked) {
      const safe = getViewportSafePos(pos);
      setPos(safe);
      localStorage.setItem('statusCardPos', JSON.stringify(safe));
    }
  }, [isDesktop, docked]);

  useEffect(() => {
    if (!shouldUseRnd) return undefined;

    const onResize = () => {
      setPos((current) => {
        const safe = getViewportSafePos(current);
        localStorage.setItem('statusCardPos', JSON.stringify(safe));
        return safe;
      });
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [shouldUseRnd]);

  const handleCloseCard = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }

    dispatch(devicesActions.clearSelection());
  }, [dispatch, onClose]);

  const handlePinToggle = useCallback(() => {
    const next = !pinned;
    setPinned(next);
    localStorage.setItem('statusPinned', next ? '1' : '0');
  }, [pinned]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleCloseCard();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleCloseCard]);

  const locked = detectLocked(device, position);

  const handleRemove = useCatch(async (removedOk) => {
    if (removedOk) {
      const response = await fetchOrThrow('/api/devices');
      dispatch(devicesActions.refresh(await response.json()));
    }
    setRemoving(false);
  });

  const handleOpenRemove = useCallback(() => {
    if (!canRemoveDevice) {
      return;
    }
    setRemoving(true);
  }, [canRemoveDevice]);

  const handleGeofence = useCatchCallback(async () => {
    if (position?.latitude == null || position?.longitude == null) return;

    const newItem = {
      name: t('sharedGeofence'),
      area: `CIRCLE (${position.latitude} ${position.longitude}, 50)`,
    };

    const response = await fetchOrThrow('/api/geofences', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newItem),
    });
    const item = await response.json();

    await fetchOrThrow('/api/permissions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        deviceId: position.deviceId,
        geofenceId: item.id,
      }),
    });

    navigate(`/settings/geofence/${item.id}`);
  }, [navigate, position, t]);

  const copyAddress = useCallback(async () => {
    const ok = await safeClipboardWrite(position?.address || '');
    setToast(ok ? 'Endereço copiado' : 'Não foi possível copiar');
  }, [position?.address]);

  const copyCoords = useCallback(async () => {
    const coords = `${position?.latitude},${position?.longitude}`;
    const ok = await safeClipboardWrite(coords);
    setToast(ok ? 'Coordenadas copiadas' : 'Não foi possível copiar');
  }, [position?.latitude, position?.longitude]);

  const quickLinks = position ? buildMapsLinks(position) : null;

  const openMapLink = (url, provider) => {
    if (!url) return;
    const result = openUrl(url, { preferSameTab: false });
    if (!result.openedInNewTab) {
      setFailedMapUrl(url);
      reportMapOpenFailure({
        source: 'status_card',
        provider,
        url,
        deviceId,
        positionId: position?.id,
      });
    }
  };

  const attrs = position?.attributes || {};
  const heroState = position ? getVehicleState(position, device, classes) : null;
  const speedKph = position ? toKph(position.speed) : null;
  const speedLabel = speedKph != null ? `${Math.round(speedKph)} km/h` : '— km/h';
  const battery = attrs.batteryLevel;
  const charging = attrs.charge;
  const lastCommunication = position?.serverTime || position?.deviceTime || device?.lastUpdate;
  const communicatedAt = formatCardDate(lastCommunication);
  const relativeCommunication = formatRelativeTime(lastCommunication);
  const driverUniqueId = attrs.driverUniqueId;
  const ignitionValue = attrs.ignition === true ? 'Ligada' : attrs.ignition === false ? 'Desligada' : '—';
  const lockedValue = locked ? 'Bloqueado' : 'Liberado';
  const compactMobile = isMobile && !mobileExpanded;
  const compactActions = isMobile || isTablet;

  const openCommand = () => {
    navigate(`/settings/device/${deviceId}/command`);
  };

  const openNavigation = (event) => {
    if (navigationAppTitle && navigationAppLink && position) {
      openMapLink(
        navigationAppLink
          .replace('{latitude}', position.latitude)
          .replace('{longitude}', position.longitude),
        'configured_navigation_app',
      );
      return;
    }
    if (quickLinks?.waze) {
      openMapLink(quickLinks.waze, 'waze');
      return;
    }
    setMapsAnchorEl(event.currentTarget);
  };

  const handleDragStop = useCallback((_, d) => {
    if (!isDesktop) return;
    const safe = getViewportSafePos({ x: d.x, y: d.y });
    setPos(safe);
    localStorage.setItem('statusCardPos', JSON.stringify(safe));
  }, [isDesktop]);

  const renderExtraMenuItems = () => (
    <>
      <MenuItem
        onClick={() => {
          setMoreAnchorEl(null);
          openCommand();
        }}
        disabled={disableActions}
      >
        <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary={t('commandTitle')} />
      </MenuItem>

      {!readonly && (
        <MenuItem
          onClick={() => {
            setMoreAnchorEl(null);
            handleGeofence();
          }}
        >
          <ListItemIcon><LocationOnIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('sharedCreateGeofence')} />
        </MenuItem>
      )}

      <MenuItem
        onClick={() => {
          setMoreAnchorEl(null);
          setMapsAnchorEl(document.body);
        }}
      >
        <ListItemIcon><MapIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary="Abrir em mapas…" />
      </MenuItem>

      <MenuItem
        onClick={() => {
          setMoreAnchorEl(null);
          copyCoords();
        }}
      >
        <ListItemIcon><MyLocationIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary="Copiar coordenadas" />
      </MenuItem>

      <MenuItem
        onClick={() => {
          setMoreAnchorEl(null);
          navigate(`/settings/device/${deviceId}`);
        }}
        disabled={!canEditDevice}
      >
        <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary={t('sharedEdit')} />
      </MenuItem>

      {canRemoveDevice && (
        <MenuItem
          onClick={() => {
            setMoreAnchorEl(null);
            handleOpenRemove();
          }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primary={t('sharedRemove')} />
        </MenuItem>
      )}

      {'share' in navigator && (
        <MenuItem
          onClick={() => {
            setMoreAnchorEl(null);
            navigator.share?.({
              title: device?.name,
              text: 'Localização atual',
              url: quickLinks?.googleShare,
            });
          }}
        >
          <ListItemIcon><MoreVertIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Compartilhar…" />
        </MenuItem>
      )}

      {!shareDisabled && !user?.temporary && (
        <MenuItem
          onClick={() => {
            setMoreAnchorEl(null);
            navigate(`/settings/device/${deviceId}/share`);
          }}
        >
          <ListItemText primary={t('deviceShare')} />
        </MenuItem>
      )}
    </>
  );

  if (!device) {
    return (
      <>
        <div className={classes.root}>
          <Card elevation={docked ? 0 : 3} className={classes.card}>
            <CardContent className={classes.content}>
              <Typography variant="body2" color="text.secondary">
                Dispositivo não encontrado.
              </Typography>
            </CardContent>
          </Card>
        </div>

        <Snackbar
          open={!!toast}
          autoHideDuration={1800}
          message={toast}
          onClose={() => setToast('')}
        />
      </>
    );
  }

  const cardContent = (
    <Card elevation={docked ? 0 : 3} className={classes.card}>
      {docked && isMobile && (
        <Box className={classes.mobileGrip}>
          <Box className={classes.mobileGripBar} />
        </Box>
      )}

      {deviceImage ? (
        <>
          <div
            className={`${classes.header} ${shouldUseRnd ? 'draggable-header' : ''}`}
            onDoubleClick={() => position && navigate(`/position/${position.id}`)}
            title="Duplo clique para abrir detalhes"
          >
            <div className={classes.headerIdentity}>
              <span className={classes.statusDot} />
              <Box className={classes.headerTitleWrap}>
                <Typography variant="h6" noWrap className={classes.headerTitle}>
                  {device?.name}
                </Typography>
                <Typography className={classes.headerSubtitle} noWrap>
                  {[heroState?.label, relativeCommunication].filter(Boolean).join(' • ')}
                </Typography>
              </Box>
            </div>

            <div className={classes.headerButtons}>
              {isMobile && mobileExpanded && onCollapse && (
                <Tooltip title="Minimizar">
                  <IconButton
                    size="small"
                    onClick={onCollapse}
                    aria-label="Minimizar"
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {shouldUseRnd && (
                <Tooltip title={pinned ? 'Desfixar' : 'Fixar'}>
                  <IconButton size="small" onClick={handlePinToggle} aria-label="Fixar card">
                    {pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}


              <IconButton
                size="small"
                onClick={handleCloseCard}
                aria-label="Fechar"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>

          <CardMedia
            className={`${classes.media} ${shouldUseRnd ? 'draggable-header' : ''}`}
            image={`/api/media/${device.uniqueId}/${deviceImage}`}
          />
        </>
      ) : (
        <div
          className={`${classes.header} ${shouldUseRnd ? 'draggable-header' : ''}`}
          onDoubleClick={() => position && navigate(`/position/${position.id}`)}
          title="Duplo clique para abrir detalhes"
        >
          <div className={classes.headerIdentity}>
            <span className={classes.statusDot} />
            <Box className={classes.headerTitleWrap}>
              <Typography variant="h6" noWrap className={classes.headerTitle}>
                {device?.name}
              </Typography>
              <Typography className={classes.headerSubtitle} noWrap>
                {[heroState?.label, relativeCommunication].filter(Boolean).join(' • ')}
              </Typography>
            </Box>
          </div>

          <div className={classes.headerButtons}>
            {isMobile && mobileExpanded && onCollapse && (
              <Tooltip title="Minimizar">
                <IconButton
                  size="small"
                  onClick={onCollapse}
                  aria-label="Minimizar"
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {shouldUseRnd && (
              <Tooltip title={pinned ? 'Desfixar' : 'Fixar'}>
                <IconButton size="small" onClick={handlePinToggle} aria-label="Fixar card">
                  {pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}


            <IconButton
              size="small"
              onClick={handleCloseCard}
              aria-label="Fechar"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      )}

      {position && heroState && (
        <>
          <Box className={classes.hero}>
            <Box className={classes.heroState}>
              <Box className={classes.heroStateIcon}>
                {heroState.icon}
              </Box>
              <Typography className={classes.heroStateLabel} noWrap>
                {heroState.label}
              </Typography>
            </Box>

            <Box className={classes.heroMetrics}>
              <Typography className={classes.speedText}>
                {speedLabel}
              </Typography>
              {locked ? (
                <LockIcon className={`${classes.lockLocked} ${classes.statusIconLarge}`} />
              ) : (
                <LockOpenIcon className={`${classes.lockUnlocked} ${classes.statusIconLarge}`} />
              )}
              {getBatteryIcon(battery, charging, classes)}
            </Box>
          </Box>
          <Box className={classes.heroDivider} />
        </>
      )}

      {position ? (
        <>
          <CardContent className={classes.content}>
            {!compactMobile && (
              <>
                <Box className={classes.decisionGrid}>
                  <Box className={classes.decisionChip}>
                    <Typography className={classes.chipLabel}>Ignição</Typography>
                    <Typography className={classes.chipValue}>{ignitionValue}</Typography>
                  </Box>
                  <Box className={classes.decisionChip}>
                    <Typography className={classes.chipLabel}>Bloqueio</Typography>
                    <Typography className={classes.chipValue}>{lockedValue}</Typography>
                  </Box>
                  <Box className={classes.decisionChip}>
                    <Typography className={classes.chipLabel}>Sinal</Typography>
                    <Typography className={classes.chipValue}>{relativeCommunication}</Typography>
                  </Box>
                </Box>

                <Box className={classes.addressCard}>
                  <Typography className={classes.addressLabel}>Endereço</Typography>
                  <Typography component="div" variant="body2" className={classes.addressValue}>
                    {position?.address || '—'}
                  </Typography>
                  <Box className={classes.addressActions}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={copyAddress}
                      disabled={!position?.address}
                    >
                      Copiar
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<MapIcon />}
                      onClick={(e) => setMapsAnchorEl(e.currentTarget)}
                    >
                      Abrir no mapa
                    </Button>
                  </Box>
                </Box>

                <Button
                  className={classes.technicalToggle}
                  variant="outlined"
                  size="small"
                  endIcon={<KeyboardArrowDownIcon sx={{ transform: technicalOpen ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }} />}
                  onClick={() => setTechnicalOpen((open) => !open)}
                >
                  Detalhes técnicos
                </Button>

                <Collapse in={technicalOpen} timeout="auto" unmountOnExit>
                  <Box className={classes.detailsGrid} sx={{ mt: 1 }}>
                    <DetailRow label="Motorista">
                      {driverUniqueId ? (
                        <PositionValue position={position} attribute="driverUniqueId" />
                      ) : '—'}
                    </DetailRow>
                    <DetailRow label="Hora do servidor">{communicatedAt}</DetailRow>
                    <DetailRow label="Distância total">
                      <PositionValue
                        position={position}
                        property={Object.prototype.hasOwnProperty.call(position, 'totalDistance') ? 'totalDistance' : null}
                        attribute={Object.prototype.hasOwnProperty.call(position.attributes || {}, 'totalDistance') ? 'totalDistance' : null}
                      />
                    </DetailRow>
                    <DetailRow label="Protocolo">{position.protocol || attrs.protocol || '—'}</DetailRow>
                    <DetailRow label="Coordenadas">
                      <Link component="button" underline="hover" onClick={copyCoords} title="Copiar coordenadas">
                        {position.latitude}, {position.longitude}
                      </Link>
                    </DetailRow>
                    <DetailRow label="ID da posição">{position.id || '—'}</DetailRow>
                  </Box>
                  <Box className={classes.detailsLinkRow}>
                    <Typography variant="body2">
                      <Link component={RouterLink} to={`/position/${position.id}`}>
                        {t('sharedShowDetails')}
                      </Link>
                    </Typography>
                  </Box>
                </Collapse>
              </>
            )}
          </CardContent>
        </>
      ) : (
        <CardContent className={classes.content}>
          <Typography variant="body2" color="text.secondary">
            Sem posição disponível.
          </Typography>
        </CardContent>
      )}

      <CardActions classes={{ root: classes.actions }} disableSpacing>
        <Box className={classes.footerPrimaryActions}>
          <Button
            className={classes.footerButton}
            variant="outlined"
            size="small"
            startIcon={<MyLocationIcon />}
            onClick={openNavigation}
            disabled={!position}
          >
            {compactActions ? 'Navegar' : 'Navegar até o veículo'}
          </Button>

          <Button
            className={classes.commandButton}
            variant="contained"
            size="small"
            startIcon={<SendIcon />}
            onClick={openCommand}
            disabled={disableActions}
          >
            {t('commandTitle')}
          </Button>
        </Box>

        {!compactMobile && (
          <Box className={classes.footerSecondaryActions}>
            <Tooltip title={t('reportReplay')}>
              <Button
                className={classes.footerButton}
                size="small"
                variant="text"
                startIcon={<RouteIcon />}
                onClick={() => navigate(`/replay?deviceId=${deviceId}`)}
                disabled={disableActions || !position}
              >
                Replay
              </Button>
            </Tooltip>
            <Tooltip title="Copiar endereço">
              <IconButton size="small" onClick={copyAddress} disabled={!position?.address} aria-label="Copiar endereço">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Mais ações">
              <IconButton size="small" onClick={(e) => setMoreAnchorEl(e.currentTarget)} disabled={!position} aria-label="Mais ações">
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </CardActions>
    </Card>
  );

  return (
    <>
      <div className={classes.root}>
        {docked || !shouldUseRnd ? (
          cardContent
        ) : (
          <Rnd
            default={getViewportSafePos(undefined)}
            enableResizing={false}
            disableDragging={pinned}
            dragHandleClassName="draggable-header"
            bounds="window"
            onDragStop={handleDragStop}
            position={pos}
            style={{ position: 'relative' }}
          >
            {cardContent}
          </Rnd>
        )}
      </div>

      {position && (
        <>
          <Menu
            anchorEl={moreAnchorEl}
            open={Boolean(moreAnchorEl)}
            onClose={() => setMoreAnchorEl(null)}
          >
            {renderExtraMenuItems()}
          </Menu>

          <Menu
            anchorEl={mapsAnchorEl}
            open={Boolean(mapsAnchorEl)}
            onClose={() => setMapsAnchorEl(null)}
          >
            <MenuItem
              onClick={() => {
                setMapsAnchorEl(null);
                openMapLink(quickLinks?.googleMaps, 'google_maps');
              }}
            >
              <ListItemIcon>
                <LocationOnIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Google Maps"
                secondary="Abrir localização no Google Maps"
              />
            </MenuItem>

            <MenuItem
              onClick={() => {
                setMapsAnchorEl(null);
                openMapLink(quickLinks?.appleMaps, 'apple_maps');
              }}
            >
              <ListItemIcon>
                <LocationOnIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Apple Maps"
                secondary="Abrir localização no Apple Maps"
              />
            </MenuItem>

            <MenuItem
              onClick={() => {
                setMapsAnchorEl(null);
                openMapLink(quickLinks?.streetView, 'street_view');
              }}
            >
              <ListItemIcon>
                <ExploreIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Street View"
                secondary="Abrir visão da rua"
              />
            </MenuItem>

            <MenuItem
              onClick={() => {
                setMapsAnchorEl(null);
                openMapLink(quickLinks?.waze, 'waze');
              }}
            >
              <ListItemIcon>
                <MyLocationIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Waze"
                secondary="Navegar até o veículo"
              />
            </MenuItem>

            <MenuItem
              onClick={async () => {
                setMapsAnchorEl(null);
                await copyCoords();
              }}
            >
              <ListItemIcon>
                <ContentCopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Copiar coordenadas"
                secondary="Latitude e longitude"
              />
            </MenuItem>

            {navigationAppTitle && navigationAppLink && (
              <MenuItem
                onClick={() => {
                  setMapsAnchorEl(null);
                  openMapLink(
                    navigationAppLink
                      .replace('{latitude}', position.latitude)
                      .replace('{longitude}', position.longitude),
                    'configured_navigation_app',
                  );
                }}
              >
                <ListItemIcon>
                  <MapIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={navigationAppTitle}
                  secondary="Abrir no app configurado"
                />
              </MenuItem>
            )}
          </Menu>
        </>
      )}

      {canRemoveDevice && (
        <RemoveDialog
          open={removing}
          endpoint="devices"
          itemId={deviceId}
          onResult={(removedOk) => handleRemove(removedOk)}
        />
      )}

      <Dialog
        open={Boolean(failedMapUrl)}
        onClose={() => setFailedMapUrl('')}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Não foi possível abrir o app de mapas</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Abra o link manualmente:
          </Typography>
          <Link href={failedMapUrl} target="_blank" rel="noopener noreferrer">
            {failedMapUrl}
          </Link>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              await copyCoords();
            }}
          >
            Copiar coordenadas
          </Button>
          <Button
            onClick={async () => {
              const ok = await safeClipboardWrite(failedMapUrl);
              setToast(ok ? 'URL copiada' : 'Não foi possível copiar');
            }}
          >
            Copiar URL
          </Button>
          <Button onClick={() => setFailedMapUrl('')}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={1800}
        message={toast}
        onClose={() => setToast('')}
      />
    </>
  );
};

export default StatusCard;
